import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Listens for incoming pushes & taps on the vendor device and:
 *  - refreshes the in-app notifications list when a push arrives,
 *  - deep-links to the relevant vendor screen on tap.
 *
 * No-op on web / Lovable preview iframe.
 */
export function useVendorPushHandler() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanup: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const recvSub = await PushNotifications.addListener(
          'pushNotificationReceived',
          () => {
            if (cancelled) return;
            qc.invalidateQueries({ queryKey: ['notifications'] });
          }
        );

        const tapSub = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            const data = (action.notification?.data || {}) as Record<string, string>;
            if (data.trip_id) {
              navigate(`/vendor/trips?trip=${encodeURIComponent(data.trip_id)}`);
            } else {
              navigate('/vendor/notifications');
            }
          }
        );

        cleanup.push(recvSub, tapSub);
      } catch (err) {
        console.warn('[vendor-push] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      cleanup.forEach((l) => {
        try { l.remove(); } catch { /* ignore */ }
      });
    };
  }, [navigate, qc]);
}